import api, { API_URL } from './api.js';
import { tenantSlugFromHostname } from '../validators.js';
import { getDevPortalAccessToken } from '../devPortalSession.js';
import { notifyNetworkError } from '../toast.js';
import { adminMfaCodeFrom, requestElevation } from './adminMfaGate.js';

/**
 * Institutions Service
 * API wrappers for the institution portal (super-admin + institution-admin) and
 * student sponsorship. Endpoints: api/src/modules/institutions/routes.js.
 *
 * `institutionId` is optional on the institution-scoped calls: an institution
 * admin omits it (the server scopes to their own record, resolved from the
 * X-Institution-Slug header the axios interceptor stamps from the hostname); a
 * super-admin passes it to drill into a specific school (the middleware reads it
 * from the ?institutionId= query param via the scoped() helper).
 */

const scoped = (institutionId, extraParams = {}) => {
  const params = { ...extraParams };
  if (institutionId) {
    params.institutionId = institutionId;
  }
  return { params };
};

// ============================================================================
// Public branding (no auth; portal shell reads this to theme itself)
// ============================================================================

// The endpoint is browser-cached for 5 minutes. `fresh` busts it so the
// portal shows a just-saved branding edit; the pre-auth hop keeps the cache.
export const getPublicBranding = async (slug, { fresh = false } = {}) => {
  const response = await api.get(
    `/institutions/branding/${slug}`,
    fresh ? { params: { v: Date.now() } } : undefined,
  );
  return response.data;
};

export const getInstitutionLogoUrl = (slug, logoFileId) => {
  if (!slug || !logoFileId) { return null; }
  const baseUrl = API_URL.replace(/\/$/, '');
  return `${baseUrl}/institutions/branding/${encodeURIComponent(slug)}/logo?v=${encodeURIComponent(logoFileId)}`;
};

export const getAutomaticInstitutionLogoUrl = (domain) => {
  if (!domain) { return null; }
  const baseUrl = API_URL.replace(/\/$/, '');
  // &v= is the cache-buster the main client and booking client also send
  // (LOGO_URL_VERSION there): the proxy answers with a 30-day browser cache.
  return `${baseUrl}/company-logo?domain=${encodeURIComponent(domain)}&v=4`;
};

export const uploadInstitutionLogo = async (file, institutionId) => {
  const formData = new FormData();
  formData.append('logo', file);
  const response = await api.post('/institutions/branding/logo', formData, {
    ...scoped(institutionId),
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const updateInstitutionBranding = async (payload, institutionId) => {
  const response = await api.patch('/institutions/branding', payload, scoped(institutionId));
  return response.data;
};

export const setInstitutionLogoSource = async (logoSource, institutionId) => {
  return updateInstitutionBranding({ logoSource }, institutionId);
};

export const renameInstitutionPortalSlug = async (slug, institutionId) => {
  const response = await api.patch('/institutions/branding/slug', { slug }, scoped(institutionId));
  return response.data;
};

// ============================================================================
// Admin context
// ============================================================================

export const getInstitutionContext = async () => {
  const response = await api.get('/institutions/me/context');
  return response.data;
};

// ============================================================================
// Super-admin (Tier 1): /admin/institutions/*, ADMIN_EMAILS-gated
// ============================================================================

export const getSuperAdminSummary = async () => {
  const response = await api.get('/admin/institutions/summary');
  return response.data;
};

export const getSuperAdminMetrics = async () => {
  const response = await api.get('/admin/institutions/metrics');
  return response.data;
};

export const getSuperAdminTimeseries = async (weeks) => {
  const response = await api.get('/admin/institutions/timeseries', { params: weeks ? { weeks } : {} });
  return response.data;
};

export const getSuperAdminCogs = async (days) => {
  const response = await api.get('/admin/institutions/cogs', { params: days ? { days } : {} });
  return response.data;
};

// Deleted accounts and canceled subscribers: lifetime COGS and net revenue.
export const getChurnEconomics = async () => {
  const response = await api.get('/admin/institutions/churn-economics');
  return response.data;
};

export const getFinancials = async () => {
  const response = await api.get('/admin/institutions/financials');
  return response.data;
};

export const setFinancials = async ({ monthlyFixedCostsCents, cashBalanceCents }) => {
  const response = await api.put('/admin/institutions/financials', { monthlyFixedCostsCents, cashBalanceCents });
  return response.data;
};

export const listInstitutions = async () => {
  const response = await api.get('/admin/institutions');
  return response.data;
};

export const createInstitution = async (payload) => {
  const response = await api.post('/admin/institutions', payload);
  return response.data;
};

export const getInstitutionDetail = async (id) => {
  const response = await api.get(`/admin/institutions/${id}`);
  return response.data;
};

export const setInstitutionBranding = async (id, payload) => {
  const response = await api.patch(`/admin/institutions/${id}/branding`, payload);
  return response.data;
};

export const editInstitutionDomains = async (id, verifiedDomains) => {
  const response = await api.patch(`/admin/institutions/${id}/domains`, { verifiedDomains });
  return response.data;
};

export const setInstitutionStatus = async (id, status) => {
  const response = await api.patch(`/admin/institutions/${id}/status`, { status });
  return response.data;
};

// PATCH /admin/institutions/:id/contract-value: the school's annual contract
// value in whole cents, or null to clear it (COF-591, F4).
export const setInstitutionContractValue = async (id, contractAnnualCents) => {
  const response = await api.patch(`/admin/institutions/${id}/contract-value`, { contractAnnualCents });
  return response.data;
};

// PATCH /admin/institutions/:id/slug — rename the school's subdomain. The old
// slug is retired, not dropped: it keeps resolving so live links and
// already-sent invite emails survive the rename.
export const renameInstitutionSlug = async (id, slug) => {
  const response = await api.patch(`/admin/institutions/${id}/slug`, { slug });
  return response.data;
};

export const listPendingAdmins = async () => {
  const response = await api.get('/admin/institutions/admin-requests');
  return response.data;
};

export const approveAdmin = async (adminId, { role, scopes }) => {
  const response = await api.post(`/admin/institutions/admin-requests/${adminId}/approve`, { role, scopes });
  return response.data;
};

export const rejectAdmin = async (adminId) => {
  const response = await api.post(`/admin/institutions/admin-requests/${adminId}/reject`);
  return response.data;
};

// ============================================================================
// Institution-admin (Tier 2): institutionId optional (super-admin drill-in via
// scoped(); institution admins omit it and rely on the X-Institution-Slug header)
// ============================================================================

export const requestAdminAccess = async () => {
  const response = await api.post('/institutions/admin-requests');
  return response.data;
};

export const submitInstitutionSupportRequest = async ({ type, subject, message, attachments = [] }) => {
  const formData = new FormData();
  formData.append('type', type);
  formData.append('subject', subject);
  formData.append('message', message);
  attachments.forEach((file) => { formData.append('attachments', file); });
  const response = await api.post('/institutions/support-requests', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const sendInstitutionAiMessage = async ({ message, history = [], mode = 'balanced', attachmentIds = [] }) => {
  const response = await api.post('/institutions/ai/chat', { message, history, mode, attachmentIds });
  return response.data;
};

export const listInstitutionAiConversations = async () => {
  const response = await api.get('/institutions/ai/conversations');
  return response.data;
};

export const createInstitutionAiConversation = async ({ title, signal } = {}) => {
  const response = await api.post('/institutions/ai/conversations', { title }, { signal });
  return response.data;
};

export const getInstitutionAiConversation = async (conversationId) => {
  const response = await api.get(`/institutions/ai/conversations/${conversationId}`);
  return response.data;
};

export const cancelInstitutionAiTurn = async (conversationId, turnId) => {
  const response = await api.post(`/institutions/ai/conversations/${conversationId}/turns/${turnId}/cancel`, {});
  return response.data;
};

const institutionAuthHeaders = () => {
  const headers = {};
  const tenantSlug = tenantSlugFromHostname();
  const authRealm = import.meta.env.VITE_AUTH_REALM || '';
  const devAccessToken = import.meta.env.DEV ? getDevPortalAccessToken() : null;
  if (tenantSlug) { headers['X-Institution-Slug'] = tenantSlug; }
  if (authRealm) { headers['X-Auth-Realm'] = authRealm; }
  if (devAccessToken) { headers.Authorization = `Bearer ${devAccessToken}`; }
  return headers;
};

const institutionStreamHeaders = () => {
  return {
    ...institutionAuthHeaders(),
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
};

const requestAdminElevationFor = async (status, data) => {
  const code = adminMfaCodeFrom({ response: { status, data } });
  return code ? requestElevation(code) : false;
};

const sendInstitutionAiAttachment = (file, { onProgress, mfaRetried }) => {
  const formData = new FormData();
  formData.append('file', file);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/institutions/ai/attachments`);
    xhr.withCredentials = true;
    Object.entries(institutionAuthHeaders()).forEach(([name, value]) => {
      xhr.setRequestHeader(name, value);
    });
    if (xhr.upload && typeof onProgress === 'function') {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) { onProgress(event.loaded / event.total); }
      };
    }
    xhr.onload = async () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText); } catch { data = {}; }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data);
        return;
      }
      if (!mfaRetried) {
        try {
          if (await requestAdminElevationFor(xhr.status, data)) {
            sendInstitutionAiAttachment(file, { onProgress, mfaRetried: true }).then(resolve, reject);
            return;
          }
        } catch {
          // Preserve the original HTTP failure if the dialog cannot settle.
        }
      }
      const error = new Error(data.error || 'Could not upload the file.');
      error.response = { status: xhr.status, data };
      reject(error);
    };
    xhr.onerror = () => { reject(new Error('Could not upload the file.')); };
    xhr.send(formData);
  });
};

export const uploadInstitutionAiAttachment = async (file, { onProgress } = {}) => {
  return sendInstitutionAiAttachment(file, { onProgress, mfaRetried: false });
};

export const getInstitutionAiAttachmentUrl = (fileId, { download = false } = {}) => {
  if (!fileId) { return null; }
  const baseUrl = API_URL.replace(/\/$/, '');
  return `${baseUrl}/institutions/ai/attachments/${encodeURIComponent(fileId)}/preview${download ? '?download=1' : ''}`;
};

export const streamInstitutionAiMessage = async ({
  message, history = [], mode = 'balanced', attachmentIds = [], conversationId = null, parentId = null,
  onDelta, onThinking, onTurn, onStatus, signal,
}) => {
  let response;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      response = await fetch(`${API_URL}/institutions/ai/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: institutionStreamHeaders(),
        body: JSON.stringify({ message, history, mode, attachmentIds, conversationId, parentId }),
        signal,
      });
    } catch (error) {
      if (error?.name !== 'AbortError') { notifyNetworkError(); }
      throw error;
    }

    if (response.ok && response.body) { break; }
    const data = await response.json().catch(() => { return {}; });
    if (attempt === 0 && await requestAdminElevationFor(response.status, data)) {
      continue;
    }
    const error = new Error(data.error || 'Could not reach Coffee Chats AI.');
    error.response = { status: response.status, data };
    throw error;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let donePayload = null;
  const consume = (eventText) => {
    const line = eventText.split('\n').find((value) => { return value.startsWith('data: '); });
    if (!line) { return; }
    let payload;
    try { payload = JSON.parse(line.slice(6)); } catch { return; }
    if (payload.type === 'delta') { onDelta?.(payload.content || ''); }
    else if (payload.type === 'thinking') { onThinking?.(payload.content || ''); }
    else if (payload.type === 'turn') { onTurn?.(payload); }
    else if (payload.type === 'status') { onStatus?.(payload); }
    else if (payload.type === 'done') {
      // Sync path emits `answer`; worker path emits `content`. Normalize both.
      donePayload = { ...payload, answer: payload.answer || payload.content };
    }
    else if (payload.type === 'error') {
      const error = new Error(payload.error || 'Coffee Chats AI ran into a problem.');
      error.midStream = true;
      // AI kill switch (AI_GENERATION_DISABLED): keep the code so the shell
      // shows the paused notice instead of a failed reply.
      if (payload.code) { error.code = payload.code; }
      throw error;
    }
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (done) { break; }
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() || '';
    events.forEach(consume);
  }
  if (buffer.trim()) { consume(buffer); }
  if (!donePayload) {
    const error = new Error('The connection dropped mid-reply. Please try again.');
    error.midStream = true;
    throw error;
  }
  return donePayload;
};

export const reattachInstitutionAiTurn = async ({
  conversationId, turnId, lastEventId = '0', onDelta, onThinking, onStatus, signal,
}) => {
  let response;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      response = await fetch(
        `${API_URL}/institutions/ai/conversations/${encodeURIComponent(conversationId)}/turns/${encodeURIComponent(turnId)}/stream?lastEventId=${encodeURIComponent(lastEventId)}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: institutionAuthHeaders(),
          signal,
        },
      );
    } catch (error) {
      if (error?.name !== 'AbortError') { notifyNetworkError(); }
      throw error;
    }

    if (response.status === 410) { return { reload: true, conversationId }; }
    if (response.ok && response.body) { break; }
    const data = await response.json().catch(() => { return {}; });
    if (attempt === 0 && await requestAdminElevationFor(response.status, data)) {
      continue;
    }
    const error = new Error(data.error || 'Could not reconnect to Coffee Chats AI.');
    error.response = { status: response.status, data };
    throw error;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let donePayload = null;
  const consume = (eventText) => {
    const line = eventText.split('\n').find((value) => { return value.startsWith('data: '); });
    if (!line) { return; }
    let payload;
    try { payload = JSON.parse(line.slice(6)); } catch { return; }
    if (payload.type === 'delta') { onDelta?.(payload.content || ''); }
    else if (payload.type === 'thinking') { onThinking?.(payload.content || ''); }
    else if (payload.type === 'status') { onStatus?.(payload); }
    else if (payload.type === 'done') {
      donePayload = { ...payload, answer: payload.answer || payload.content };
    }
    else if (payload.type === 'error') {
      const error = new Error(payload.error || 'Coffee Chats AI ran into a problem.');
      error.midStream = true;
      // AI kill switch (AI_GENERATION_DISABLED): keep the code so the shell
      // shows the paused notice instead of a failed reply.
      if (payload.code) { error.code = payload.code; }
      throw error;
    }
  };
  for (;;) {
    const { value, done } = await reader.read();
    if (done) { break; }
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() || '';
    events.forEach(consume);
  }
  if (buffer.trim()) { consume(buffer); }
  return donePayload || { reload: true, conversationId };
};

// includeNonProgram is honored for platform super-admins only; the API ignores
// it for school admins.
export const getInstitutionAnalytics = async (segment, institutionId, weeks, includeNonProgram) => {
  const response = await api.get('/institutions/analytics', scoped(institutionId, {
    ...(segment ? { segment } : {}),
    ...(weeks ? { weeks } : {}),
    ...(includeNonProgram ? { includeNonProgram: 1 } : {}),
  }));
  return response.data;
};

export const getSeats = async (institutionId) => {
  const response = await api.get('/institutions/seats', scoped(institutionId));
  return response.data;
};

export const getInstitutionInvoices = async (institutionId, limit = 12) => {
  const response = await api.get('/institutions/invoices', scoped(institutionId, { limit }));
  return response.data;
};

export const getSuperAdminInstitutionBilling = async (institutionId, limit = 24) => {
  const response = await api.get(`/admin/institutions/${institutionId}/billing`, { params: { limit } });
  return response.data;
};

export const refundInstitutionInvoice = async (
  institutionId,
  invoiceId,
  { amountCents, reason = 'requested_by_customer', requestId },
) => {
  const response = await api.post(
    `/admin/institutions/${institutionId}/invoices/${invoiceId}/refund`,
    { amountCents, reason, requestId },
  );
  return response.data;
};

export const requestInstitutionPermission = async ({ requestedScope, reason = '' }, institutionId) => {
  const response = await api.post(
    '/institutions/permission-requests',
    { requestedScope, reason },
    scoped(institutionId),
  );
  return response.data;
};

export const listMyPermissionRequests = async (institutionId) => {
  const response = await api.get('/institutions/permission-requests/mine', scoped(institutionId));
  return response.data;
};

export const listPermissionRequests = async (institutionId) => {
  const response = await api.get('/institutions/permission-requests', scoped(institutionId));
  return response.data;
};

export const approvePermissionRequest = async (requestId, institutionId, note = '') => {
  const response = await api.post(
    `/institutions/permission-requests/${requestId}/approve`,
    { note },
    scoped(institutionId),
  );
  return response.data;
};

export const rejectPermissionRequest = async (requestId, institutionId, note = '') => {
  const response = await api.post(
    `/institutions/permission-requests/${requestId}/reject`,
    { note },
    scoped(institutionId),
  );
  return response.data;
};

export const listInstitutionUsers = async (institutionId, page = 1, search = '', view = 'opted_in') => {
  const response = await api.get('/institutions/users', scoped(institutionId, {
    page,
    ...(search ? { search } : {}),
    ...(view !== 'opted_in' ? { view } : {}),
  }));
  return response.data;
};

export const adjustSeats = async (seatsPurchased, institutionId, seatPlan) => {
  const response = await api.patch('/institutions/seats', {
    seatsPurchased,
    ...(seatPlan ? { seatPlan } : {}),
  }, scoped(institutionId));
  return response.data;
};

export const revokeSeat = async (userId, institutionId) => {
  const response = await api.post('/institutions/seats/revoke', { userId }, scoped(institutionId));
  return response.data;
};

export const setOveragePool = async ({ enabled, capCents }, institutionId) => {
  const response = await api.patch('/institutions/overage-pool', { enabled, capCents }, scoped(institutionId));
  return response.data;
};

// Whether sponsored students' extra job credits may draw the overage pool.
// The school's own admins set it directly; it needs no funding request.
export const setJobCreditPool = async (enabled, institutionId) => {
  const response = await api.patch('/institutions/overage-pool/job-credits', { enabled }, scoped(institutionId));
  return response.data;
};

/**
 * Open student requests to the school. Returns { openCount, requests }.
 * Each request has kind 'SEAT' | 'PLAN' (no kind means SEAT); openCount
 * counts SEAT requests only. The COUNT is the number that justifies buying
 * seats; the messages are context for the ones who wrote one.
 */
export const listSeatRequests = async (institutionId) => {
  const response = await api.get('/institutions/seat-requests', scoped(institutionId));
  return response.data;
};

export const resolveSeatRequest = async (requestId, status, institutionId) => {
  const response = await api.post(
    `/institutions/seat-requests/${requestId}/resolve`, { status }, scoped(institutionId),
  );
  return response.data;
};

export const listFundingRequests = async (institutionId) => {
  const response = await api.get('/institutions/funding-requests', scoped(institutionId));
  return response.data;
};

export const submitFundingRequest = async ({ type, payload }, institutionId) => {
  const response = await api.post('/institutions/funding-requests', { type, payload }, scoped(institutionId));
  return response.data;
};

export const listPendingFundingRequests = async () => {
  const response = await api.get('/admin/institutions/funding-requests');
  return response.data;
};

export const approveFundingRequest = async (requestId, note = '') => {
  const response = await api.post(`/admin/institutions/funding-requests/${requestId}/approve`, { note });
  return response.data;
};

export const rejectFundingRequest = async (requestId, note = '') => {
  const response = await api.post(`/admin/institutions/funding-requests/${requestId}/reject`, { note });
  return response.data;
};

export const listAdmins = async (institutionId) => {
  const response = await api.get('/institutions/admins', scoped(institutionId));
  return response.data;
};

export const inviteAdmin = async ({ email, role, scopes }, institutionId) => {
  const response = await api.post('/institutions/admins', { email, role, scopes }, scoped(institutionId));
  return response.data;
};

export const changeAdminScopes = async (adminId, scopes, institutionId) => {
  const response = await api.patch(`/institutions/admins/${adminId}`, { scopes }, scoped(institutionId));
  return response.data;
};

export const changeAdminRole = async (adminId, role, institutionId) => {
  const response = await api.patch(`/institutions/admins/${adminId}/role`, { role }, scoped(institutionId));
  return response.data;
};

export const revokeInstitutionAdmin = async (adminId, institutionId) => {
  const response = await api.delete(`/institutions/admins/${adminId}`, scoped(institutionId));
  return response.data;
};

export const acceptInvite = async (token) => {
  const response = await api.post('/institutions/admins/accept', { token });
  return response.data;
};

export const getAuditLog = async (institutionId, limit) => {
  const response = await api.get('/institutions/audit-log', scoped(institutionId, limit ? { limit } : {}));
  return response.data;
};

export const verifyInstitutionAuditChain = async (institutionId) => {
  const response = await api.get('/institutions/audit-chain/verify', scoped(institutionId));
  return response.data;
};

// ============================================================================
// Student sponsorship
// ============================================================================

export const getSponsorshipOffer = async () => {
  const response = await api.get('/institutions/sponsorship/offer');
  return response.data;
};

export const optInSponsorship = async () => {
  const response = await api.post('/institutions/sponsorship/opt-in');
  return response.data;
};

export const optOutSponsorship = async () => {
  const response = await api.post('/institutions/sponsorship/opt-out');
  return response.data;
};
